<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid positions-modal"
         @close="close">
    <template #header>
      <div class="p-dialog-title p-d-flex p-ai-center" v-if="itemData?.id">
        <div>{{ $t('Invoice') }} {{ itemData.number }}</div>
        <ChangedAfterExportIcon :show="itemData.changed_after_export"/>
      </div>
      <div class="p-dialog-title" v-else>{{ $t('Invoice') }}</div>
    </template>

    <Spinner v-if="modalDataIsLoading || dataIsSending"/>

    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <InputText id="billing-invoice-company"
                     :modelValue="companyName"
                     disabled/>
          <label for="billing-invoice-company">{{ $t('Company') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <InputText id="billing-invoice-number"
                     v-model.trim="itemData.number"
                     disabled
                     :class="{'p-invalid': submitted && !itemData.number}"
                     maxlength="64"
                     autocomplete="off"/>
          <label for="billing-invoice-number">
            {{ $t('Number') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.number">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="billing-invoice-state"
                          v-model="selectedState"
                          :class="{'p-invalid': submitted && !selectedState}"
                          :options="BillingInvoiceStates"
                          :preventFilter="true"
                          :filter="false"
                          :showClear="false"
                          disabled>
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span class="state-badge" :class="slotProps.value.color">
                  {{ slotProps.value.label[$i18n.locale] }}
                </span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>

            <template #option="slotProps">
              <div>
                <span class="state-badge" :class="slotProps.option.color">
                  {{ slotProps.option.label[$i18n.locale] }}
                </span>
              </div>
            </template>
          </CustomDropdown>
          <label for="billing-invoice-state">
            {{ $t('Status') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedState">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <Calendar id="billing-invoice-date"
                    appendTo="self"
                    class="calendar-bottom"
                    v-model="invoiceDate"
                    :class="{'p-invalid': (invoiceDate && !invoiceDateIsValid) || (submitted && !invoiceDate) || (invoiceDate && dueAt && dueAt < invoiceDate)}"
                    :dateFormat="settings.dateFormat"
                    :showIcon="true"
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :showButtonBar="true"
                    :yearRange="(new Date().getFullYear() - 5) + ':' + (new Date().getFullYear() + 1)"
                    :selectOtherMonths="true"
                    autocomplete="off"
                    @date-select="changeInvoiceDate"
                    @blur="changeInvoiceDate"
                    @show="toggleDropdownOverlayVisibility(true)"
                    @hide="toggleDropdownOverlayVisibility(false)"/>
          <label for="billing-invoice-date">
            {{ $t('issuedLabel') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small class="p-invalid" v-if="submitted && !invoiceDate">{{ $t('Required field') }}</small>
        <small class="p-invalid" v-if="invoiceDate && !invoiceDateIsValid">{{ $t('Invalid date format') }}</small>
      </div>

      <div class="p-field p-col-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <Calendar id="billing-invoice-due-at"
                    appendTo="self"
                    class="calendar-bottom"
                    v-model="dueAt"
                    :class="{'p-invalid': (dueAt && !dueAtIsValid) || (submitted && !dueAt) || (invoiceDate && dueAt && dueAt < invoiceDate)}"
                    :dateFormat="settings.dateFormat"
                    :showIcon="true"
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :showButtonBar="true"
                    :yearRange="(new Date().getFullYear() - 5) + ':' + (new Date().getFullYear() + 1)"
                    :selectOtherMonths="true"
                    autocomplete="off"
                    @date-select="changeDueAt"
                    @blur="changeDueAt"
                    @show="toggleDropdownOverlayVisibility(true)"
                    @hide="toggleDropdownOverlayVisibility(false)"/>
          <label for="billing-invoice-due-at">
            {{ $t('paymentDueLabel') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small class="p-invalid" v-if="submitted && !dueAt">{{ $t('Required field') }}</small>
        <small class="p-invalid" v-if="dueAt && !dueAtIsValid">{{ $t('Invalid date format') }}</small>
      </div>

      <div class="p-field p-col-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <InputNumber id="billing-invoice-paid-amount"
                       v-model="itemData.paid_amount"
                       :locale="computedNumberInputLocale"
                       mode="decimal"
                       :min="0"
                       :minFractionDigits="2"
                       :maxFractionDigits="2"
                       autocomplete="off"/>
          <label for="billing-invoice-paid-amount">{{ $t('Paid') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12">
        <div class="p-field p-d-flex p-ai-center p-jc-between add-position-buttons">
          <Button type="button"
                  style="width: auto"
                  class="p-button-outlined p-button-sm"
                  @click="addLine">
            <i class="ti-plus p-mr-1"/>
            <span>{{ $t('Add') }}</span>
          </Button>
        </div>

        <div class="table-wrapper p-mb-5"
             @dragover.prevent="handleSortableBodyDragOver"
             @drop.prevent="handleSortableBodyDrop">
          <table class="p-datatable p-datatable-striped p-datatable-sm p-datatable-hoverable-rows positions-table">
            <thead class="p-datatable-thead">
            <tr>
              <th class="table-title__icon"></th>
              <!--              <th class="table-title_g">Sorting</th>-->
              <th class="table-title__name--first" style="width: 32%">{{ $t('Description') }}</th>
              <th class="table-title__qty" style="width: 10%">{{ $t('Qty') }}</th>
              <th class="table-title__unit-price" style="width: 11%">{{ $t('Price') }}</th>
              <th class="table-title__discount" style="width: 10%">
                {{ $t('Discount') }}<span style="font-weight: 300">(%)</span>
              </th>
              <th class="table-title__sum" style="width: 11%">{{ $t('Sum') }}</th>
              <th class="table-title__tax" style="width: 11%">{{ $t('Tax') }} %</th>
              <th class="table-title__total" style="width: 12%">{{ $t('Total') }}</th>
              <th class="table-title__delete" style="width: 30px"></th>
            </tr>
            </thead>

            <tbody class="p-datatable-tbody">
            <!--            <tr v-if="!lines.length">-->
            <!--              <td colspan="8" class="p-text-center p-text-secondary p-p-3">-->
            <!--              </td>-->
            <!--            </tr>-->

            <tr v-for="(line, index) in lines"
                :key="line.local_id || line.id || index"
                class="sortable-row"
                :class="getSortableRowDragClass(index)"
                @dragenter.prevent="handleSortableDragEnter($event, index)"
                @dragover.prevent="handleSortableDragOver($event, index)"
                @dragleave="handleSortableDragLeave($event, index)"
                @drop.prevent="handleSortableDrop($event, index)">
              <td class="position-type-sort-cell">
                <div class="position-sort-wrap">
                  <button type="button"
                          class="position-sort-handle"
                          :class="{ 'is-disabled': !canSortRows() }"
                          :draggable="canSortRows()"
                          :disabled="!canSortRows()"
                          @dragstart="handleSortableDragStart($event, index)"
                          @dragend="handleSortableDragEnd">
                    <i class="ti-hand-drag"/>
                  </button>

                  <div class="position-sort-actions" v-if="canSortRows() && showSortableStepButtons()">
                    <button type="button"
                            :disabled="!canMoveSortableRowStep(index, -1)"
                            @click="moveSortableRowStep(index, -1)">
                      <i class="ti-angle-up"/>
                    </button>

                    <button type="button"
                            :disabled="!canMoveSortableRowStep(index, 1)"
                            @click="moveSortableRowStep(index, 1)">
                      <i class="ti-angle-down"/>
                    </button>
                  </div>
                </div>
              </td>

              <!--              <td>-->
              <!--                {{line.sorting}}-->
              <!--              </td>-->

              <td>
                <InputText v-model.trim="line.description"
                           class="table-body__description-input"
                           :class="{'p-invalid': submitted && !line.description}"
                           maxlength="255"
                           autocomplete="off"/>
              </td>

              <td>
                <InputNumber v-model="line.quantity"
                             class="table-body__number-input"
                             :locale="computedNumberInputLocale"
                             mode="decimal"
                             :min="0"
                             :max="99999"
                             :showButtons="true"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             autocomplete="off"
                             @input="lineNumberOnInput(line, 'quantity', $event.value)"/>
              </td>

              <td>
                <InputNumber v-model="line.unit_price"
                             class="table-body__number-input"
                             :locale="computedNumberInputLocale"
                             mode="decimal"
                             :minFractionDigits="2"
                             :maxFractionDigits="4"
                             :min="0"
                             :max="1000000"
                             autocomplete="off"
                             @input="lineNumberOnInput(line, 'unit_price', $event.value)"/>
              </td>

              <td>
                <InputNumber v-model="line.discount_percent"
                             class="table-body__number-input"
                             :locale="computedNumberInputLocale"
                             mode="decimal"
                             :minFractionDigits="0"
                             :maxFractionDigits="2"
                             :min="0"
                             :max="100"
                             autocomplete="off"
                             @input="lineNumberOnInput(line, 'discount_percent', $event.value)"/>
              </td>

              <td>
                <InputNumber v-model="line.subtotal"
                             class="table-body__number-input"
                             :locale="computedNumberInputLocale"
                             mode="decimal"
                             disabled
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             :min="0"
                             :max="1000000"
                             autocomplete="off"/>
              </td>

              <td>
                <InputNumber v-model="line.tax_rate"
                             class="table-body__number-input"
                             :locale="computedNumberInputLocale"
                             mode="decimal"
                             :minFractionDigits="0"
                             :maxFractionDigits="2"
                             :min="0"
                             :max="100"
                             autocomplete="off"
                             @input="lineNumberOnInput(line, 'tax_rate', $event.value)"/>
              </td>

              <td>
                <InputNumber v-model="line.total"
                             class="table-body__total-sum-input"
                             :locale="computedNumberInputLocale"
                             mode="decimal"
                             disabled
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             autocomplete="off"/>
              </td>

              <td class="p-text-right">
                <Button type="button"
                        class="p-button-text p-button-danger p-button-sm"
                        style="margin: 0 3px;"
                        icon="ti-trash"
                        :disabled="lines.length <= 1"
                        @click="removeLine(index)"/>
              </td>
            </tr>

            <!--            <tr class="billing-invoice-modal__summary-row">-->
            <!--              <td colspan="8">-->
            <!--                <ul class="billing-invoice-modal__summary">-->
            <!--                  <li>-->
            <!--                    <span>{{ $t('Sum without tax') }}:</span>-->
            <!--                    <strong>{{ formatCurrency(summary.subtotal) }}</strong>-->
            <!--                  </li>-->
            <!--                  <li>-->
            <!--                    <span>{{ $t('Tax') }}:</span>-->
            <!--                    <strong>{{ formatCurrency(summary.tax) }}</strong>-->
            <!--                  </li>-->
            <!--                  <li>-->
            <!--                    <span>{{ $t('Total') }}:</span>-->
            <!--                    <strong>{{ formatCurrency(summary.total) }}</strong>-->
            <!--                  </li>-->
            <!--                  <li>-->
            <!--                    <span>{{ $t('Paid') }}:</span>-->
            <!--                    <strong>{{ formatCurrency(itemData.paid_amount || 0) }}</strong>-->
            <!--                  </li>-->
            <!--                  <li>-->
            <!--                    <span>{{ $t('Unpaid') }}:</span>-->
            <!--                    <strong>{{ formatCurrency(unpaidAmount) }}</strong>-->
            <!--                  </li>-->
            <!--                </ul>-->
            <!--              </td>-->
            <!--            </tr>-->

            <tr style="background-color: inherit">
              <!--              <td style="vertical-align:top; line-height: 1.5;" colspan="3">-->
              <!--                <div :class="{'warning-color' : summary.totalTime > maxTotalTime}"-->
              <!--                     v-if="summary.totalTime && +summary.totalTime">-->
              <!--                  &lt;!&ndash;                  {{ $t('Total time') }}: {{ formatDecimal(summary.totalTime) }} {{ $t('h') }}&ndash;&gt;-->
              <!--                  {{ $t('Total time') }}: {{ formatHoursAndMinutes(summary.totalTime) }}-->
              <!--                </div>-->
              <!--              </td>-->
              <td style="padding-right: 10px; line-height: 1.5; text-align: right;" colspan="16">
                <ul class="p-text-right p-d-flex p-flex-column p-align-end">
                  <li class="p-d-flex p-ai-center">
                    <div>{{ $t('Sum without tax') }}: </div>
                    <div class="table-summary__price">{{ formatCurrency(summary.subtotal) }}</div>
                  </li>
                  <li class="p-d-flex p-ai-center">
                    <div>{{ $t('Tax') }}: </div>
                    <div class="table-summary__price">{{ formatCurrency(summary.tax) }}</div>
                  </li>
                  <li class="p-d-flex p-ai-center p-text-bold">
                    <div>{{ $t('Grand total') }}: </div>
                    <div class="table-summary__price">{{ formatCurrency(summary.total) }}</div>
                  </li>

                  <!--                  <li>-->
                  <!--                    <div>{{ $t('Paid') }}: </div>-->
                  <!--                    <div class="table-summary__price">{{ formatCurrency(itemData.paid_amount || 0) }}</div>-->
                  <!--                  </li>-->
                  <!--                  <li class="p-text-secondary">
                                      <div>{{ $t('Unpaid') }}: </div>
                                      <div class="table-summary__price">{{ formatCurrency(unpaidAmount) }}</div>
                                    </li>-->
                  <!--                  <li class="p-d-flex p-ai-center p-text-secondary">-->
                  <!--                    <div>{{ $t('Unpaid') }}: </div>-->
                  <!--                    <div class="table-summary__price">{{ formatCurrency(unpaidAmount) }}</div>-->
                  <!--                  </li>-->

                  <!--                  <li v-if="unpaidAmount > 0"-->
                  <!--                      class="p-d-flex p-ai-center p-text-secondary">-->
                  <!--                    <div>{{ $t('Unpaid') }}:</div>-->
                  <!--                    <div class="table-summary__price">-->
                  <!--                      {{ formatCurrency(unpaidAmount) }}-->
                  <!--                    </div>-->
                  <!--                  </li>-->

                  <!--                  <li v-if="overpaidAmount > 0"-->
                  <!--                      class="p-d-flex p-ai-center warning-color">-->
                  <!--                    <div>{{ $t('Overpaid') }}:</div>-->
                  <!--                    <div class="table-summary__price">-->
                  <!--                      {{ formatCurrency(overpaidAmount) }}-->
                  <!--                    </div>-->
                  <!--                  </li>-->

                  <li class="p-d-flex p-ai-center p-text-secondary">
                    <div>{{ $t('Due') }}:</div>

                    <div class="table-summary__price">
                      {{ formatCurrency(balanceAmount) }}
                    </div>
                  </li>
                </ul>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="p-field p-col-12">
        <LimitedCharsTextarea :submitted="submitted"
                              :rows="2"
                              v-model="itemData.internal_comment"
                              :maxChars="internalCommentMaxChars"
                              id="billing-invoice-modal-notes"
                              :label="$t('Notes')"/>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>

      <Button :label="$t('Save')"
              :disabled="dataIsSending"
              :loading="dataIsSending"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import settings from '@/settings'
import formatMixins from '@/mixins/formatMixins'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import overlayVisibilityMixins from '@/mixins/overlayVisibilityMixins'
import ChangedAfterExportIcon from '@/components/ChangedAfterExportIcon.vue'
import BillingInvoiceStates from '@/translations/states/BillingInvoiceStates'
import sortingMixins from "@/mixins/sortingMixins";

const BILLING_LINE_TYPE = 6

export default {
  name: 'BillingInvoiceModal',
  emits: ['close', 'update-items'],
  components: {
    ChangedAfterExportIcon
  },
  mixins: [
    formatMixins,
    showErrorsMixins,
    overlayVisibilityMixins,
    sortingMixins
  ],
  props: {
    item: Object,
    visible: Boolean
  },
  data() {
    return {
      settings,
      BillingInvoiceStates,

      modalDataIsLoading: false,
      dataIsSending: false,
      submitted: false,

      itemData: {},
      lines: [],
      selectedState: null,

      invoiceDate: null,
      dueAt: null,
      invoiceDateIsValid: false,
      dueAtIsValid: false,

      internalCommentMaxChars: 512,

      summary: {
        subtotal: 0,
        tax: 0,
        total: 0
      }
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.loadItem()
      } else {
        this.reset()
      }
    },
    item: {
      immediate: true,
      handler() {
        if (this.visible) {
          this.loadItem()
        }
      }
    }
  },
  methods: {
    async loadItem() {
      this.reset()

      if (!this.item) {
        return
      }

      this.itemData = { ...this.item }

      if (this.item.id) {
        await this.loadFullInvoice(this.item.id)
      } else {
        this.fillFromInvoice(this.item)
      }
    },

    async loadFullInvoice(id) {
      this.modalDataIsLoading = true

      try {
        const { status, data } = await httpClient({
          url: 'billing-invoice-system/view',
          method: 'post',
          data: { id }
        })

        if (status === 200 && data?.success && data.invoice) {
          this.fillFromInvoice(data.invoice)
        } else {
          this.showUnknownError()
          this.fillFromInvoice(this.item)
        }
      } catch (err) {
        this.showError(err)
        this.fillFromInvoice(this.item)
      } finally {
        this.modalDataIsLoading = false
      }
    },

    fillFromInvoice(invoice) {
      this.itemData = {
        ...invoice,
        paid_amount: invoice.paid_amount ? +invoice.paid_amount : 0,
        internal_comment: invoice.internal_comment ?? null
      }

      this.selectedState = invoice.state
          ? this.BillingInvoiceStates.find(state => +state.num === +invoice.state) || null
          : null

      this.invoiceDate = invoice.invoice_date
          ? this.timestampToUtcDayDate(invoice.invoice_date)
          : this.normalizeLocalDay(new Date())

      this.dueAt = invoice.due_at
          ? this.timestampToUtcDayDate(invoice.due_at)
          : null

      this.checkInvoiceDateIsValid()
      this.checkDueAtIsValid()

      this.lines = Array.isArray(invoice.lines) && invoice.lines.length
          ? invoice.lines.map(this.normalizeLine)
          : [this.createEmptyLine()]

      this.normalizeLinesSorting()
      this.updateSummary()
    },

    normalizeLine(line) {
      return {
        id: line.id ?? null,
        local_id: line.local_id || this.generateLocalId(),
        type: line.type ? +line.type : BILLING_LINE_TYPE,
        description: line.description ?? '',
        quantity: line.quantity ? +line.quantity : 0,
        unit_price: line.unit_price ? +line.unit_price : 0,
        discount_percent: line.discount_percent ? +line.discount_percent : 0,
        discount_amount: line.discount_amount ? +line.discount_amount : 0,
        subtotal: line.subtotal ? +line.subtotal : 0,
        tax_rate: line.tax_rate ? +line.tax_rate : 0,
        tax: line.tax ? +line.tax : 0,
        total: line.total ? +line.total : 0,
        sorting: line.sorting ? +line.sorting : null,
        metadata_json: line.metadata_json ?? null
      }
    },

    createEmptyLine() {
      return {
        id: null,
        local_id: this.generateLocalId(),
        type: BILLING_LINE_TYPE,
        description: '',
        quantity: 1,
        unit_price: 0,
        discount_percent: 0,
        discount_amount: 0,
        tax_rate: 0,
        subtotal: 0,
        tax: 0,
        total: 0,
        sorting: this.findLinesLastSortingValue(),
        metadata_json: null
      }
    },

    generateLocalId() {
      return 'new-' + Math.random().toString(36).slice(2)
    },

    findLinesLastSortingValue() {
      if (!this.lines?.length) {
        return 1
      }

      const maxSorting = this.lines.reduce((max, line) => {
        const sorting = Number(line?.sorting)
        return Number.isFinite(sorting) ? Math.max(max, sorting) : max
      }, 0)

      return Math.max(maxSorting, this.lines.length) + 1
    },

    normalizeLinesSorting() {
      this.lines.forEach((line, index) => {
        line.sorting = index + 1
      })
    },

    addLine() {
      this.lines.push(this.createEmptyLine())
      this.normalizeLinesSorting()
      this.updateSummary()
    },

    removeLine(index) {
      if (this.lines.length <= 1) {
        return
      }

      this.lines.splice(index, 1)
      this.normalizeLinesSorting()
      this.updateSummary()
    },

    lineNumberOnInput(line, field, value) {
      if (!line || !field) return

      line[field] = this.toNumber(value)

      const quantity = this.toNumber(line.quantity)
      const unitPrice = this.toNumber(line.unit_price)
      const taxRate = this.toNumber(line.tax_rate)

      let discountPercent = this.toNumber(line.discount_percent)

      if (discountPercent < 0) {
        discountPercent = 0
      }

      if (discountPercent > 100) {
        discountPercent = 100
      }

      const baseSubtotal = this.toMoney(quantity * unitPrice)
      const discountAmount = this.toMoney(baseSubtotal * discountPercent / 100)
      const subtotal = this.toMoney(Math.max(0, baseSubtotal - discountAmount))
      const tax = this.toMoney(subtotal * taxRate / 100)

      line.discount_percent = discountPercent
      line.discount_amount = discountAmount
      line.subtotal = subtotal
      line.tax = tax
      line.total = this.toMoney(subtotal + tax)

      this.updateSummary()
    },

    updateSummary() {
      let subtotal = 0
      let tax = 0
      let total = 0

      this.lines.forEach(line => {
        subtotal += +line.subtotal || 0
        tax += +line.tax || 0
        total += +line.total || 0
      })

      this.summary.subtotal = this.toMoney(subtotal)
      this.summary.tax = this.toMoney(tax)
      this.summary.total = this.toMoney(total)

      this.itemData.subtotal = this.summary.subtotal
      this.itemData.tax = this.summary.tax
      this.itemData.total = this.summary.total
    },

    changeInvoiceDate() {
      this.invoiceDate = this.normalizeLocalDay(this.invoiceDate)
      this.checkInvoiceDateIsValid()
    },

    changeDueAt() {
      this.dueAt = this.normalizeLocalDay(this.dueAt)
      this.checkDueAtIsValid()
    },

    checkInvoiceDateIsValid() {
      this.invoiceDateIsValid = this.checkDateIsValid(this.invoiceDate)
    },

    checkDueAtIsValid() {
      this.dueAtIsValid = this.checkDateIsValid(this.dueAt)
    },

    checkDateIsValid(date) {
      return !!(date instanceof Date && !isNaN(date))
    },

    normalizeLocalDay(date) {
      if (!this.checkDateIsValid(date)) {
        return date
      }

      date.setHours(0, 0, 0, 0)
      return date
    },

    timestampToUtcDayDate(timestamp) {
      if (!timestamp) {
        return null
      }

      const d = new Date(Number(timestamp) * 1000)

      return new Date(
          d.getUTCFullYear(),
          d.getUTCMonth(),
          d.getUTCDate(),
          0,
          0,
          0,
          0
      )
    },

    toUtcDayTimestamp(date, endOfDay = false) {
      if (!this.checkDateIsValid(date)) {
        return null
      }

      return Math.floor(Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          endOfDay ? 23 : 0,
          endOfDay ? 59 : 0,
          endOfDay ? 59 : 0
      ) / 1000)
    },

    toMoney(value) {
      return Math.round((+value || 0) * 100) / 100
    },
    toUnitPrice(value) {
      return Math.round((+value || 0) * 10000) / 10000
    },

    validate() {
      this.submitted = true

      this.checkInvoiceDateIsValid()
      this.checkDueAtIsValid()

      const invalidLine = this.lines.some(line => !line.description)

      if (invalidLine) {
        this.$toast.add({
          severity: 'error',
          detail: this.$t('Required field'),
          life: settings.toastLife
        })
      }

      return !!(
          this.itemData.number &&
          this.selectedState &&
          this.invoiceDateIsValid &&
          this.dueAtIsValid &&
          this.dueAt >= this.invoiceDate &&
          !invalidLine
      )
    },

    buildPayload() {
      this.normalizeLinesSorting()
      this.updateSummary()

      return {
        id: this.itemData.id ? +this.itemData.id : null,
        number: this.itemData.number ? String(this.itemData.number).trim() : null,

        invoice_date: this.toUtcDayTimestamp(this.invoiceDate),
        due_at: this.toUtcDayTimestamp(this.dueAt, true),

        subtotal: this.summary.subtotal,
        tax: this.summary.tax,
        total: this.summary.total,

        paid_amount: this.itemData.paid_amount ? this.toMoney(this.itemData.paid_amount) : 0,
        internal_comment: this.itemData.internal_comment
            ? String(this.itemData.internal_comment).trim()
            : null,

        lines: this.lines.map((line, index) => ({
          id: line.id ? +line.id : null,
          type: line.type ? +line.type : BILLING_LINE_TYPE,
          description: line.description ? String(line.description).trim() : null,
          quantity: this.toMoney(line.quantity),
          unit_price: this.toUnitPrice(line.unit_price),
          discount_percent: this.toMoney(line.discount_percent),
          discount_amount: this.toMoney(line.discount_amount),
          tax_rate: this.toMoney(line.tax_rate),
          subtotal: this.toMoney(line.subtotal),
          tax: this.toMoney(line.tax),
          total: this.toMoney(line.total),
          metadata_json: line.metadata_json ?? null,
          sorting: line.sorting ? +line.sorting : index + 1
        }))
      }
    },

    async saveItem() {
      if (!this.validate()) {
        return
      }

      // if (+this.itemData.state !== 1 && this.itemData.id) {
      //   const confirmed = window.confirm(this.$t('Invoice has already been issued. Continue editing?'))
      //
      //   if (!confirmed) {
      //     return
      //   }
      // }

      this.dataIsSending = true

      try {
        const { status, data } = await httpClient.post('billing-invoice-system/update', this.buildPayload())

        if (status === 200 && data?.success) {
          this.$emit('update-items', this.itemData.id)
          this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife})

          this.close()
        } else if (data?.errors) {
          this.$toast.add({severity: 'error', detail: JSON.stringify(data.errors), life: settings.toastLife})
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.dataIsSending = false
      }
    },

    close() {
      this.$emit('close')
      this.reset()
    },

    reset() {
      this.modalDataIsLoading = false
      this.dataIsSending = false
      this.submitted = false

      this.itemData = {}
      this.lines = []
      this.clearSortableDragState()
      this.selectedState = null

      this.invoiceDate = null
      this.dueAt = null
      this.invoiceDateIsValid = false
      this.dueAtIsValid = false

      this.summary = {
        subtotal: 0,
        tax: 0,
        total: 0
      }
    },
    getSortableRows() {
      return this.lines || []
    },

    isSortableRowVisible(line) {
      return !!line
    },

    isSortableRowsDisabled() {
      return !!(
          this.dataIsSending ||
          this.modalDataIsLoading
      )
    },

    afterSortableRowsChanged() {
      this.normalizeLinesSorting()
      this.updateSummary()
    },

    showSortableStepButtons() {
      return true
    },
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: 0, width: '96%', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        // return {position: 'absolute', top: 0, width: '94%', maxWidth: this.exportInvoiceToSmartAccounts ? '1600px' : '1420px', marginTop: '15px'}
        return {position: 'absolute', top: 0, width: '94%', maxWidth: '1400px', marginTop: '15px'}
      } else {
        // return {position: 'absolute', top: 0, width: '90%', maxWidth: this.exportInvoiceToSmartAccounts ? '1600px' : '1420px'}
        return {position: 'absolute', top: 0, width: '90%', maxWidth: '1400px'}
      }
    },
    companyName() {
      return this.itemData.company_name
          || this.itemData.tenant_company?.name
          || this.itemData.buyer_name
          || null
    },
    balanceAmount() {
      const amount = this.toMoney(
          (+this.summary.total || 0)
          - (+this.itemData.paid_amount || 0)
      )

      return Math.abs(amount) < 0.01 ? 0 : amount
    },
  },
}
</script>

<style scoped lang="scss">
.table-body__description-input {
  min-width: 200px;
  //max-width: 280px;
  //width: 14vw;
  width: 100%;
}
.table-body__number-input {
  min-width: 85px;
  //max-width: 144px;
  //width: 8vw;
  width: 100%;
}
.table-body__total-sum-input {
  min-width: 95px;
  //max-width: 144px;
  //width: 8vw;
  width: 100%;
}
</style>